import { Badge, Box, Button, Center, Group, Image, Overlay, Stack, Text } from '@mantine/core'
import React, { useEffect, useMemo, useState } from 'react'
import { CheckCircleFill, CheckCircleOutline } from 'antd-mobile-icons'
import { useSearchParams } from 'react-router-dom'
import { payment } from '../api/orderService'
import { getDictByKey } from '../api/dictionaryService'
import { Toast } from 'antd-mobile'
import LegalNotice from '../QueryReport/components/LegalNotice'
import WechatPayQRCodeModal from './components/WechatPayQRCodeModal'

interface ChannelOption {
  key: string
  title: string
  image: string
  router: string
  desc?: string
}

interface PayChannel {
  key: string
  router: string
  weight: number
  allowedDomains?: string[]
}

interface PaymentType {
  type: string
  title: string
  image: string
  desc?: string
  channels: PayChannel[]
}

export default function Payment() {
  const [time, setTime] = useState('5:00')
  const [selected, setSelected] = useState<ChannelOption>()
  const [loading, setLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get('orderId') || ''
  const templateName = searchParams.get('templateName') || ''
  const templatePrice = searchParams.get('templatePrice') || ''
  const [channel, setChannel] = useState<ChannelOption[]>([])
  const routers = useMemo(() => searchParams.get('routers')?.split(',') || [], [searchParams])
  const [showWxAlert, setShowWxAlert] = useState(false)
  const [showQRCode, setShowQRCode] = useState(false)
  const [qrCodeUrl, setQrCodeUrl] = useState('')

  const isEnvWeixin = /MicroMessenger/i.test(window.navigator.userAgent)

  useEffect(() => {
    getDictByKey('key_pay_channel')
      .then(async (data) => {
        let paymentTypes: PaymentType[] = JSON.parse(data)
        let availableChannels: ChannelOption[] = []
        for (const paymentType of paymentTypes) {
          const matchedChannel = paymentType.channels.find((channel) => routers.includes(channel.router))
          if (matchedChannel) {
            availableChannels.push({
              key: matchedChannel.key,
              title: paymentType.title,
              image: paymentType.image,
              router: matchedChannel.router,
              desc: paymentType.desc,
            })
          }
        }

        setChannel(availableChannels)
        setSelected(availableChannels[0])
      })
      .catch(() => {
        Toast.show('获取支付渠道失败')
      })
  }, [routers])

  useEffect(() => {
    const timer = setInterval(() => {
      const [minutes, seconds] = time.split(':').map(Number)
      if (minutes === 0 && seconds === 0) {
        clearInterval(timer)
      } else if (seconds === 0) {
        setTime(`${minutes - 1}:59`)
      } else {
        setTime(`${minutes}:${seconds < 11 ? `0${seconds - 1}` : seconds - 1}`)
      }
    }, 1000)
    return () => clearInterval(timer)
  }, [time])

  const doPayment = async () => {
    try {
      if (isEnvWeixin && (selected?.key.startsWith('alipay') || selected?.key.startsWith('fuiouAlipay'))) {
        setShowWxAlert(true)
        return
      }

      setLoading(true)
      const res = await payment(orderId, selected?.router!)
      if (selected?.key.startsWith('fuiouAlipay')) {
        window.location.href = res
        return
      }
      if (selected?.key.startsWith('fuiouWxpay')) {
        if (isEnvWeixin) {
          window.location.href = res
        } else {
          setQrCodeUrl(res)
          setShowQRCode(true)
        }
        return
      }
      const div = document.createElement('div')
      div.innerHTML = res
      document.body.appendChild(div)
      const form = div.querySelector('form')
      form?.submit()
    } catch (error: any) {
      Toast.show(error?.message || '支付失败')
    } finally {
      setLoading(false)
    }
  }

  const CheckListItem = ({
    children,
    checked,
    onChange,
  }: {
    children: React.ReactNode
    checked: boolean
    onChange: () => void
  }) => {
    return (
      <Group
        justify="space-between"
        onClick={onChange}
        style={{
          cursor: 'pointer',
          padding: '12px 0',
        }}
      >
        {children}
        {checked ? (
          <CheckCircleFill color={'#FF8C00'} fontSize={24} />
        ) : (
          <CheckCircleOutline color={'#CCCCCC'} fontSize={24} />
        )}
      </Group>
    )
  }

  return (
    <Box style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <Box py={12} style={{ borderBottom: '1px solid #f0f0f0' }}>
        <Center>
          <Text fw={500} size="md">
            收银台
          </Text>
        </Center>
      </Box>

      <Stack gap={0}>
        <Stack align="center" gap={8} py={30}>
          <Group gap={6} align="center">
            <Text c="#333" size="md">
              支付剩余时间
            </Text>
            <Text c="#FF6B00" fw={500} size="lg" style={{ fontFamily: 'monospace' }}>
              {time}
            </Text>
          </Group>

          <Group gap={4} align="baseline" mt={4}>
            <Text fz={28} fw={700} c="#333">
              ¥
            </Text>
            <Text fz={48} fw={700} c="#333" style={{ lineHeight: 1 }}>
              {templatePrice}
            </Text>
          </Group>

          <Text c="dimmed" size="xs" mt={4}>
            您已成功提交订单，请尽快完成支付
          </Text>
        </Stack>

        <Box px={20} pb={20}>
          <Stack gap={16}>
            <Group justify="space-between" align="flex-start">
              <Text c="dimmed" size="sm" style={{ minWidth: '80px' }}>
                订单编号
              </Text>
              <Text c="#333" size="sm" style={{ fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'right' }}>
                {orderId}
              </Text>
            </Group>
            <Group justify="space-between" align="flex-start">
              <Text c="dimmed" size="sm" style={{ minWidth: '80px' }}>
                商品名称
              </Text>
              <Text c="#333" size="sm" fw={500} style={{ textAlign: 'right' }}>
                {templateName}
              </Text>
            </Group>
          </Stack>
        </Box>

        <Box h={10} bg="#F7F8FA" w="100%" />

        <Box>
          <Box px={20} py={12} style={{ borderBottom: '1px solid #f5f5f5' }}>
            <Text c="dimmed" size="sm">
              请选择支付方式
            </Text>
          </Box>

          <Box px={20}>
            <Stack gap={0}>
              {channel.map((item) => (
                <CheckListItem
                  key={item.key}
                  checked={selected?.key === item.key}
                  children={
                    <Group gap="md">
                      <Image h={28} w={28} src={item.image} fit="contain" />
                      <Stack gap={2}>
                        <Group gap={8}>
                          <Text size="md" c="#333">
                            {item.title}
                          </Text>
                          {item.desc && (
                            <Badge
                              color="#FF6B00"
                              variant="light"
                              size="sm"
                              radius="sm"
                              style={{ textTransform: 'none' }}
                            >
                              {item.desc}
                            </Badge>
                          )}
                        </Group>
                      </Stack>
                    </Group>
                  }
                  onChange={() => {
                    setSelected(item)
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      </Stack>

      <LegalNotice />

      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        p={20}
        bg="white"
        style={{ borderTop: '1px solid #f5f5f5', paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }}
      >
        <Button
          onClick={() => {
            doPayment().then()
          }}
          fullWidth
          radius="xl"
          size="lg"
          loading={loading}
          styles={{
            root: {
              backgroundColor: '#3A55DD',
              height: '50px',
              boxShadow: '0 4px 12px rgba(58, 85, 221, 0.2)',
            },
          }}
        >
          <Text fw={600} size="md">
            立即支付
          </Text>
        </Button>
      </Box>

      <Box h={100} />

      {showWxAlert && (
        <Overlay
          color="#000"
          backgroundOpacity={0.7}
          zIndex={999}
          onClick={() => {
            setShowWxAlert(false)
          }}
          style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', padding: '20px' }}
        >
          <Image src="https://file.sjcyx.com/wx_alert.png" w="80%" style={{ maxWidth: '300px' }} />
        </Overlay>
      )}

      <WechatPayQRCodeModal opened={showQRCode} onClose={() => setShowQRCode(false)} url={qrCodeUrl} />
    </Box>
  )
}
